<template>
  <div class="w-full">
    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <button
          type="button"
          class="btn"
          @click="emit('back')"
        >
          {{ t('agentEvals.back', 'Back') }}
        </button>
        <div style="margin-inline-start: 1rem">
          <div class="text-xs text-slate-500 dark:text-slate-400">{{ t('agentEvals.editing', 'Editing') }}</div>
          <div class="text-xl font-bold text-slate-900 dark:text-white">{{ evalNameTitle }}</div>
          <div v-if="latestRunAtText !== ''" class="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {{ t('agentEvals.lastRunLabel', 'Last run:') }} {{ latestRunAtText }}<span v-if="headerStatusText !== ''"> · {{ headerStatusText }}</span>
          </div>
        </div>
      </div>
      <div class="flex items-center">
        <button
          type="button"
          class="btn"
          @click="emit('openRun', evalId)"
          :disabled="evalId === ''"
        >
          {{ t('agentEvals.run', 'Run') }}
        </button>
        <button
          type="button"
          class="btn"
          style="margin-inline-start: 0.75rem"
          @click="exportEvalJson"
          :disabled="exportingEvalJson === true || evalId === ''"
        >
          {{ exportingEvalJson === true ? t('agentEvals.exporting', 'Exporting...') : t('agentEvals.exportJson', 'Export JSON') }}
        </button>
        <button
          type="button"
          class="btn"
          style="margin-inline-start: 0.75rem"
          @click="openImportPicker"
          :disabled="importingEvalJson === true || evalId === ''"
        >
          {{ importingEvalJson === true ? t('agentEvals.importing', 'Importing...') : t('agentEvals.importJson', 'Import JSON') }}
        </button>
        <button
          type="button"
          class="btn"
          style="margin-inline-start: 0.75rem"
          @click="downloadEvalPdf"
          :disabled="exportingEvalPdf === true"
        >
          {{ exportingEvalPdf === true ? t('agentEvals.exporting', 'Exporting...') : t('agentEvals.downloadEvalPdf', 'Download eval PDF') }}
        </button>
        <button
          type="button"
          class="btn"
          style="margin-inline-start: 0.75rem"
          @click="removeEval"
          :disabled="deletingEval === true"
        >
          {{ t('common.delete', 'Delete') }}
        </button>
        <input
          ref="importFileInputRef"
          type="file"
          accept="application/json"
          class="hidden"
          @change="handleImportFile"
        />
      </div>
    </div>

    <div ref="evalPdfTargetRef" class="fixed left-[-10000px] top-0 w-[794px] bg-white text-slate-900" :dir="isRtl === true ? 'rtl' : 'ltr'">
      <div class="p-10">
        <div class="flex items-start justify-between">
          <div>
            <div class="text-3xl font-bold">{{ evalNameTitle }}</div>
            <div class="mt-2 text-sm text-slate-600">{{ t('agentEvals.evalResults', 'Eval Results') }}</div>
          </div>
          <div class="text-right">
            <div class="text-xs text-slate-500">{{ t('agentEvals.generatedAt', 'Generated at:') }}</div>
            <div class="text-sm font-semibold">{{ evalPdfGeneratedAt }}</div>
          </div>
        </div>

        <div class="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <div class="flex items-center justify-between">
            <div class="text-sm text-slate-600">
              <span class="font-semibold">{{ t('agentEvals.overallResult', 'Overall result:') }}</span>
            </div>
            <div class="text-base font-bold">
              <span v-if="headerStatusText !== ''">{{ headerStatusText }}</span>
              <span v-else>{{ t('agentEvals.notRun', 'Not run') }}</span>
            </div>
          </div>
          <div v-if="latestRunAtText !== ''" class="mt-2 text-sm text-slate-600">{{ t('agentEvals.lastRunLabel', 'Last run:') }} {{ latestRunAtText }}</div>
        </div>

        <div class="mt-6">
          <div v-for="test in tests" :key="`pdf-${test.testId}`" class="border border-slate-200 rounded-2xl p-5" style="margin-top: 0.9rem">
            <div class="flex items-start justify-between">
              <div class="font-bold text-lg">{{ test.testName }}</div>
              <div class="text-sm font-bold" style="margin-inline-start: 0.75rem">{{ statusTagText(test) }}</div>
            </div>
            <div v-if="test.lastRunAt !== undefined && test.lastRunAt !== null" class="mt-2 text-xs text-slate-500">{{ t('agentEvals.lastRunLabel', 'Last run:') }} {{ formatDate(test.lastRunAt) }}</div>

            <div class="mt-4 text-sm font-semibold">{{ t('agentEvals.agentResponse', 'Agent Response') }}</div>
            <div class="mt-2 text-sm whitespace-pre-wrap">{{ test.generatedAnswer !== null && test.generatedAnswer !== undefined ? String(test.generatedAnswer) : '' }}</div>

            <div class="mt-4 text-sm font-semibold">{{ t('agentEvals.judgeReason', 'Judge Reason') }}</div>
            <div class="mt-2 text-sm whitespace-pre-wrap">{{ test.autoReason !== null && test.autoReason !== undefined ? String(test.autoReason) : '' }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 space-y-6">
      <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
        <div class="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
          <div class="text-lg font-bold text-slate-900 dark:text-white">{{ t('agentEvals.evalDetails', 'Eval Details') }}</div>
        </div>

        <div class="px-6 py-6 space-y-5">
          <div class="space-y-2">
            <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Eval Name</label>
            <input
              type="text"
              class="w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              v-model="editEvalName"
              @blur="saveEvalDetails"
            />
          </div>

          <div class="space-y-2">
            <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Agent</label>
            <MultiSelect
              v-model="editAgentId"
              :options="agents"
              placeholder="Select agent"
              id-field="agentId"
              label-field="agentName"
              :max-selections="1"
              @update:modelValue="saveEvalDetails"
              buttonClass="!rounded-xl !border-2 !border-slate-200 dark:!border-slate-700 !bg-white dark:!bg-slate-800 !text-slate-900 dark:!text-white !px-4 !py-3 !h-auto !min-h-[50px] focus:!ring-2 focus:!ring-blue-500 focus:!border-blue-500"
            />
          </div>
        </div>
      </div>

      <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
        <div class="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div class="text-lg font-bold text-slate-900 dark:text-white">{{ t('agentEvals.tests', 'Tests') }}</div>
            <div class="flex items-center">
              <div class="flex items-center" style="margin-inline-start: 0.75rem">
                <div class="text-xs text-slate-600 dark:text-slate-300">{{ t('agentEvals.runsPerTest', 'Runs per test') }}</div>
                <select
                  v-model.number="selectedRunCount"
                  class="select select-bordered select-sm"
                  style="margin-inline-start: 0.5rem; width: 5.5rem"
                  :disabled="runningEval === true"
                >
                  <option v-for="option in runCountOptions" :key="`editor-run-count-${option}`" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>
              <button
                type="button"
                class="btn"
                @click="runAll"
                :disabled="runningEval === true || evalId === ''"
              >
                {{ runningEval === true ? t('agentEvals.running', 'Running...') : t('agentEvals.runAll', 'Run All') }}
              </button>
              <button
                type="button"
                class="btn"
                style="margin-inline-start: 0.75rem"
                @click="generateTestsWithAI"
                :disabled="generatingTests === true"
              >
                {{ generatingTests === true ? t('agentEvals.generating', 'Generating...') : t('agentEvals.generateWithAI', 'Generate with AI') }}
              </button>
              <button
                type="button"
                class="btn"
                style="margin-inline-start: 0.75rem"
                @click="addTest"
                :disabled="savingTest === true"
              >
                {{ t('agentEvals.addTest', 'Add Test') }}
              </button>
            </div>
          </div>
        </div>

        <div class="px-6 py-6">
          <div v-if="loadingEval === true" class="-m-3">
            <div v-for="n in 5" :key="`test-skel-${n}`" class="p-3">
              <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
              <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                <div class="flex-1 min-w-0">
                  <div class="h-5 w-2/3 skeleton"></div>
                  <div class="mt-4 h-6 w-24 skeleton"></div>
                </div>
                <div class="mt-4 sm:mt-0 flex items-center" style="margin-inline-start: 1rem">
                  <div class="h-9 w-20 skeleton"></div>
                  <div class="h-9 w-20 skeleton" style="margin-inline-start: 0.5rem"></div>
                </div>
              </div>
            </div>
            </div>
          </div>
          <div v-else-if="tests.length === 0" class="text-sm text-slate-500 dark:text-slate-400">{{ t('agentEvals.noTestsYet', 'No tests yet') }}</div>
          <div v-else class="space-y-5">
            <div
              v-for="test in tests"
              :key="test.testId"
              class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
            >
              <div class="px-5 py-4 cursor-pointer" @click="openTest(test)">
                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                  <div class="flex-1 min-w-0">
                    <div class="text-base font-bold text-slate-900 dark:text-white truncate">{{ test.testName }}</div>
                    <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">{{ test.config.runnerType === 'voice' ? 'Phone Voice' : 'Regular Chat' }}</div>
                    <div class="mt-2 flex items-center">
                      <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border" :class="statusTagClass(test)">
                        {{ statusTagText(test) }}
                      </span>
                    </div>
                    <div v-if="getRunTotalCount(test) > 0" class="mt-2 text-xs text-slate-600 dark:text-slate-300">
                      {{ t('agentEvals.passed', 'Passed') }}: {{ getRunPassCount(test) }} |
                      {{ t('agentEvals.failed', 'Failed') }}: {{ getRunFailCount(test) }} |
                      {{ t('agentEvals.total', 'Total') }}: {{ getRunTotalCount(test) }}
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 flex items-center" style="margin-inline-start: 1rem">
                    <button
                      type="button"
                      class="btn btn-sm"
                      @click.stop="openTest(test)"
                    >
                      {{ t('agentEvals.open', 'Open') }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      style="margin-inline-start: 0.5rem"
                      @click.stop="removeTest(test)"
                      :disabled="test.deleting === true"
                    >
                      {{ t('common.delete', 'Delete') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="errorText !== ''" class="rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-5 text-sm text-red-700 dark:text-red-300">
        {{ errorText }}
      </div>
    </div>

    <AppModal :visible="openTestModal === true" :title="activeTestTitle" boxClass="max-w-4xl w-11/12" @close="closeTest">
      <div v-if="openTestModal === true && activeTest !== null" ref="pdfTargetRef" class="p-6 space-y-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border" :class="statusTagClass(activeTest)">
              {{ statusTagText(activeTest) }}
            </span>
          </div>
          <div class="flex items-center">
            <div class="flex items-center" style="margin-inline-end: 0.5rem">
              <div class="text-xs text-slate-600 dark:text-slate-300">{{ t('agentEvals.runsPerTest', 'Runs per test') }}</div>
              <select
                v-model.number="selectedRunCount"
                class="select select-bordered select-sm"
                style="margin-inline-start: 0.5rem; width: 5.5rem"
                :disabled="activeTest.running === true"
              >
                <option v-for="option in runCountOptions" :key="`modal-editor-run-count-${option}`" :value="option">
                  {{ option }}
                </option>
              </select>
            </div>
            <button
              type="button"
              class="btn btn-sm"
              @click="runOne(activeTest, selectedRunCount)"
              :disabled="activeTest.running === true"
            >
              {{ activeTest.running === true ? t('agentEvals.running', 'Running...') : t('agentEvals.run', 'Run') }}
            </button>
            <button
              type="button"
              class="btn btn-sm"
              style="margin-inline-start: 0.5rem"
              @click="downloadActiveTestPdf"
            >
              {{ t('agentEvals.downloadPdf', 'Download PDF') }}
            </button>
            <button
              type="button"
              class="btn btn-sm"
              style="margin-inline-start: 0.5rem"
              @click="openEditModal"
            >
              {{ t('common.edit', 'Edit') }}
            </button>
          </div>
        </div>
        <div v-if="getRunTotalCount(activeTest) > 0" class="text-xs text-slate-600 dark:text-slate-300">
          {{ t('agentEvals.passed', 'Passed') }}: {{ getRunPassCount(activeTest) }} |
          {{ t('agentEvals.failed', 'Failed') }}: {{ getRunFailCount(activeTest) }} |
          {{ t('agentEvals.total', 'Total') }}: {{ getRunTotalCount(activeTest) }}
        </div>
        <div v-if="getRunTotalCount(activeTest) > 0" class="flex items-center justify-between">
          <div class="text-xs font-semibold text-slate-700 dark:text-slate-300">Run {{ activeRunPageText }}</div>
          <div class="flex items-center">
            <button
              type="button"
              class="btn btn-sm"
              @click="goToPreviousRunPage"
              :disabled="getSafeRunPage(activeTest) <= 1"
            >
              {{ t('common.previous', 'Previous') }}
            </button>
            <button
              type="button"
              class="btn btn-sm"
              style="margin-inline-start: 0.5rem"
              @click="goToNextRunPage"
              :disabled="getSafeRunPage(activeTest) >= getRunTotalCount(activeTest)"
            >
              {{ t('common.next', 'Next') }}
            </button>
          </div>
        </div>

        <div class="flex flex-col lg:flex-row mt-2">
          <div class="w-full lg:w-1/2">
            <div v-if="activeTest.config.runnerType === 'voice'" class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5" style="margin-bottom: 1rem">
              <div class="text-sm font-bold text-slate-900 dark:text-white">Phone Voice Test</div>
              <div class="mt-2 text-xs text-slate-600 dark:text-slate-300">Strategy: {{ activeTest.config.voice.strategy === 'qa' ? 'Autonomous QA Tester' : 'Hardcoded User Messages' }}</div>
              <div v-if="activeTest.config.voice.strategy === 'qa' && activeTest.config.voice.qaPrompt.trim().length > 0" class="mt-3 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{{ activeTest.config.voice.qaPrompt }}</div>
            </div>
            <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div class="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                <div class="text-sm font-bold text-slate-900 dark:text-white">Messages</div>
              </div>
              <div class="p-5 max-h-[60vh] overflow-y-auto">
                <div v-if="Array.isArray(activeTest.messages) !== true || activeTest.messages.length === 0" class="text-sm text-slate-500 dark:text-slate-400">No messages</div>
                <div v-else class="space-y-3">
                  <div v-for="(m, idx) in activeTest.messages" :key="`active-${activeTest.testId}-${idx}`" class="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4">
                    <div class="flex items-center justify-between">
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border" :class="m.role === 'agent' ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300 border-indigo-200 dark:border-indigo-900' : 'bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300 border-sky-200 dark:border-sky-900'">
                        {{ m.role === 'agent' ? 'Agent' : 'User' }}
                      </span>
                      <span class="text-[11px] text-slate-400">{{ idx + 1 }}</span>
                    </div>
                    <div class="mt-3 text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{{ m.content }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="w-full lg:w-1/2 col-gap mt-6 lg:mt-0 space-y-5">
            <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div class="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                <div class="flex items-center justify-between">
                  <div class="text-sm font-bold text-slate-900 dark:text-white">Run Status</div>
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border" :class="statusTagClass({ manualPass: null, autoPass: activeRunHistoryItem !== null ? activeRunHistoryItem.autoPass : null })">
                    {{ statusTagText({ manualPass: null, autoPass: activeRunHistoryItem !== null ? activeRunHistoryItem.autoPass : null }) }}
                  </span>
                </div>
              </div>
              <div class="p-5">
                <div v-if="activeRunHistoryItem !== null" class="text-xs text-slate-700 dark:text-slate-300">Run {{ activeRunHistoryItem.runNumber }}</div>
                <div v-else class="text-xs text-slate-500 dark:text-slate-400">No runs yet</div>
              </div>
            </div>

            <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div class="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                <div class="text-sm font-bold text-slate-900 dark:text-white">Agent Response</div>
              </div>
              <div class="p-5 max-h-[24vh] overflow-y-auto">
                <div v-if="activeRunGeneratedAnswer.trim().length > 0" class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{{ activeRunGeneratedAnswer }}</div>
                <div v-else class="text-sm text-slate-500 dark:text-slate-400">No response yet</div>
              </div>
            </div>

            <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div class="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                <div class="text-sm font-bold text-slate-900 dark:text-white">Judge Reason</div>
              </div>
              <div class="p-5 max-h-[24vh] overflow-y-auto">
                <div v-if="activeRunAutoReason.trim().length > 0" class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{{ activeRunAutoReason }}</div>
                <div v-else class="text-sm text-slate-500 dark:text-slate-400">No judge result yet</div>
              </div>
            </div>

            <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div class="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                <div class="text-sm font-bold text-slate-900 dark:text-white">Judge Prompt</div>
              </div>
              <div class="p-5 max-h-[18vh] overflow-y-auto">
                <div v-if="typeof activeTest.judgePrompt === 'string' && activeTest.judgePrompt.trim().length > 0" class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{{ activeTest.judgePrompt }}</div>
                <div v-else class="text-sm text-slate-500 dark:text-slate-400">No prompt</div>
              </div>
            </div>

            <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div class="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                <div class="text-sm font-bold text-slate-900 dark:text-white">Sources</div>
              </div>
              <div class="p-5 max-h-[18vh] overflow-y-auto">
                <div v-if="Array.isArray(activeTest.sources) === true && activeTest.sources.length > 0" class="space-y-3">
                  <div v-for="(s, idx) in activeTest.sources" :key="`src-${idx}`" class="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4">
                    <div class="text-xs font-semibold text-slate-500 dark:text-slate-400">{{ s.type !== undefined && s.type !== null ? String(s.type) : 'source' }}</div>
                    <div v-if="typeof s.url === 'string' && s.url.trim().length > 0" class="mt-2 text-sm">
                      <a class="text-blue-600 dark:text-blue-400 underline break-words" :href="s.url" target="_blank" rel="noopener noreferrer">{{ s.url }}</a>
                    </div>
                    <div v-else-if="typeof s.name === 'string' && s.name.trim().length > 0" class="mt-2 text-sm text-slate-700 dark:text-slate-300 break-words">{{ s.name }}</div>
                    <div v-if="typeof s.highlight === 'string' && s.highlight.trim().length > 0" class="mt-2 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{{ s.highlight }}</div>
                    <div v-if="s.type === 'voiceEval' && Array.isArray(s.conversation) === true" class="mt-3">
                      <div v-for="(message, messageIndex) in s.conversation" :key="`voice-message-${messageIndex}`" class="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap" style="margin-top: 0.5rem">
                        {{ message.role === 'agent' ? 'Agent' : 'QA Caller' }}: {{ message.content }}
                      </div>
                    </div>
                    <pre v-if="s.type === 'voiceEval' && Array.isArray(s.diagnostics) === true && s.diagnostics.length > 0" class="mt-3 text-xs whitespace-pre-wrap break-words">{{ JSON.stringify(s.diagnostics, null, 2) }}</pre>
                  </div>
                </div>
                <div v-else class="text-sm text-slate-500 dark:text-slate-400">No sources</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppModal>

    <AppModal :visible="openEditTestModal === true" title="Edit test" boxClass="max-w-4xl w-11/12 overflow-hidden" @close="closeEditModal">
      <div v-if="openEditTestModal === true && editDraft !== null" class="p-6">
        <div class="max-h-[72vh] overflow-y-auto">
          <div class="space-y-6">
            <div class="space-y-2">
              <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Test Name</label>
              <input
                type="text"
                class="w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-semibold"
                v-model="editDraft.testName"
              />
            </div>
            <div class="space-y-2">
              <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Judge Prompt</label>
              <textarea
                class="w-full min-h-[140px] rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                v-model="editDraft.judgePrompt"
                maxlength="3500"
              ></textarea>
            </div>
            <div>
              <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Test Runtime</label>
              <select v-model="editDraft.config.runnerType" class="select select-bordered w-full" style="margin-top: 0.5rem">
                <option value="chat">Regular Chat</option>
                <option value="voice">Phone Voice</option>
              </select>
            </div>
            <div v-if="editDraft.config.runnerType === 'voice'">
              <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Voice Test Strategy</label>
              <select v-model="editDraft.config.voice.strategy" class="select select-bordered w-full" style="margin-top: 0.5rem">
                <option value="scripted">Hardcoded User Messages</option>
                <option value="qa">Autonomous QA Tester</option>
              </select>
              <div v-if="editDraft.config.voice.strategy === 'qa'" style="margin-top: 1rem">
                <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">QA Test Query</label>
                <textarea
                  v-model="editDraft.config.voice.qaPrompt"
                  class="textarea textarea-bordered w-full min-h-[120px]"
                  style="margin-top: 0.5rem"
                  placeholder="Test whether the agent completes the requested flow and performs the expected actions"
                ></textarea>
              </div>
              <div class="flex flex-col sm:flex-row" style="margin-top: 1rem">
                <div class="w-full sm:w-1/3">
                  <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Tester Voice</label>
                  <input v-model="editDraft.config.voice.testerVoiceId" class="input input-bordered w-full" style="margin-top: 0.5rem" />
                </div>
                <div class="w-full sm:w-1/3" style="margin-inline-start: 1rem">
                  <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Language</label>
                  <input v-model="editDraft.config.voice.languageCode" class="input input-bordered w-full" style="margin-top: 0.5rem" />
                </div>
                <div class="w-full sm:w-1/3" style="margin-inline-start: 1rem">
                  <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Maximum Turns</label>
                  <input v-model.number="editDraft.config.voice.maxTurns" type="number" min="1" max="30" class="input input-bordered w-full" style="margin-top: 0.5rem" />
                </div>
              </div>
            </div>
            <div class="space-y-4">
              <div v-for="(m, idx) in editDraft.messages" :key="`edit-${editDraft.testId}-${idx}`" class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <select
                    class="rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    v-model="m.role"
                  >
                    <option value="user">User</option>
                    <option value="agent">Agent</option>
                  </select>
                  <button
                    type="button"
                    class="btn btn-sm mt-3 sm:mt-0"
                    @click="removeMessage(editDraft, idx)"
                  >
                    Remove
                  </button>
                </div>
                <textarea
                  class="mt-4 w-full min-h-[120px] rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  v-model="m.content"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex items-center justify-between">
          <div class="flex items-center">
            <button
              type="button"
              class="btn"
              @click="addMessage(editDraft, 'user')"
              :disabled="savingDraft === true"
            >
              Add User Message
            </button>
            <button
              type="button"
              class="btn"
              style="margin-inline-start: 0.75rem"
              @click="addMessage(editDraft, 'agent')"
              :disabled="savingDraft === true"
            >
              Add Agent Message
            </button>
          </div>
          <div class="flex items-center">
          <button
            type="button"
            class="btn"
            @click="closeEditModal"
            :disabled="savingDraft === true"
          >
            {{ t('common.cancel', 'Cancel') }}
          </button>
          <button
            type="button"
              class="btn"
              style="margin-inline-start: 0.75rem"
            @click="saveDraft"
            :disabled="savingDraft === true"
          >
            {{ savingDraft === true ? t('agentEvals.saving', 'Saving...') : t('agentEvals.save', 'Save') }}
          </button>
          </div>
        </div>
      </div>
    </AppModal>

    <AppModal :visible="showRunProgressModal === true" title="Running tests" boxClass="max-w-lg w-11/12" @close="closeRunProgressModal">
      <div v-if="showRunProgressModal === true" class="p-6">
        <div class="text-sm font-semibold text-slate-900 dark:text-white">Progress</div>
        <div class="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{{ runCompleted }} / {{ runTotal }}</div>
        <div class="mt-4 w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div class="h-2 rounded-full bg-blue-600" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="mt-3 text-sm text-slate-600 dark:text-slate-300">{{ runLabel }}</div>
        <div class="mt-5 flex items-center justify-end">
          <button
            type="button"
            class="btn"
            @click="closeRunProgressModal"
            :disabled="runningEval === true"
          >
            {{ t('agentEvals.close', 'Close') }}
          </button>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n.js';
import MultiSelect from '@/components/MultiSelect.vue';
import AppModal from '@/components/AppModal.vue';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';

const { t, currentLanguage } = useI18n();
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);

const props = defineProps({
  evalId: { type: String, required: true },
  agents: { type: Array, required: true }
});

const emit = defineEmits(['back', 'openRun', 'refreshList', 'setError']);

const loadingEval = ref(false);
const savingEval = ref(false);
const deletingEval = ref(false);
const savingTest = ref(false);
const generatingTests = ref(false);
const runningEval = ref(false);
const errorText = ref('');

const selectedEval = ref(null);
const tests = ref([]);
const openTestModal = ref(false);
const activeTest = ref(null);
const pdfTargetRef = ref(null);
const evalPdfTargetRef = ref(null);
const exportingEvalPdf = ref(false);
const exportingEvalJson = ref(false);
const importingEvalJson = ref(false);
const importFileInputRef = ref(null);
const evalPdfGeneratedAt = ref('');
const openEditTestModal = ref(false);
const editDraft = ref(null);
const savingDraft = ref(false);
const showRunProgressModal = ref(false);
const runTotal = ref(0);
const runCompleted = ref(0);
const runCurrentTestName = ref('');
const runCurrentIteration = ref(0);
const runCountOptions = [1, 2, 3, 5, 10];
const selectedRunCount = ref(1);
const selectedRunPage = ref(1);

function resolvedPassValue(t) {
  const manual = t !== undefined && t !== null ? (t.manualPass === true ? true : (t.manualPass === false ? false : null)) : null;
  if (manual !== null) {
    return manual;
  }
  const auto = t !== undefined && t !== null ? (t.autoPass === true ? true : (t.autoPass === false ? false : null)) : null;
  return auto;
}

function statusTagText(row) {
  const val = resolvedPassValue(row);
  if (val === true) {
    return t('agentEvals.passed', 'Passed');
  }
  if (val === false) {
    return t('agentEvals.notPassed', 'Not passed');
  }
  return t('agentEvals.notRun', 'Not run');
}

function statusTagClass(t) {
  const val = resolvedPassValue(t);
  if (val === true) {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900';
  }
  if (val === false) {
    return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 border-red-200 dark:border-red-900';
  }
  return 'bg-slate-50 text-slate-700 dark:bg-slate-800/50 dark:text-slate-200 border-slate-200 dark:border-slate-700';
}

function formatDate(value) {
  if (value === undefined || value === null) {
    return '';
  }
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime()) === true) {
      return String(value);
    }
    return d.toLocaleString();
  } catch (e) {
    return '';
  }
}

const editEvalName = ref('');
const editAgentId = ref([]);

const agents = computed(() => Array.isArray(props.agents) === true ? props.agents : []);

function getAuthToken() {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') != false)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') != false)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  if (token === undefined || token === null) {
    return '';
  }
  return token;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function normalizeTestConfig(rawConfig) {
  const config = rawConfig !== undefined && rawConfig !== null && typeof rawConfig === 'object' && Array.isArray(rawConfig) === false ? rawConfig : {};
  const voice = config.voice !== undefined && config.voice !== null && typeof config.voice === 'object' && Array.isArray(config.voice) === false ? config.voice : {};
  return {
    runnerType: config.runnerType === 'voice' ? 'voice' : 'chat',
    voice: {
      strategy: voice.strategy === 'qa' ? 'qa' : 'scripted',
      qaPrompt: typeof voice.qaPrompt === 'string' ? voice.qaPrompt : '',
      testerVoiceId: typeof voice.testerVoiceId === 'string' && voice.testerVoiceId.trim().length > 0 ? voice.testerVoiceId : 'alloy',
      languageCode: typeof voice.languageCode === 'string' && voice.languageCode.trim().length > 0 ? voice.languageCode : 'he',
      maxTurns: Number.isFinite(Number(voice.maxTurns)) === true ? Math.max(1, Math.min(30, Math.floor(Number(voice.maxTurns)))) : 8
    }
  };
}

function mapTestRow(row) {
  const msgs = Array.isArray(row.messages) === true ? row.messages : (row.messages !== undefined && row.messages !== null ? row.messages : []);
  const messages = Array.isArray(msgs) === true ? msgs.map(m => ({ role: m.role === 'agent' ? 'agent' : 'user', content: typeof m.content === 'string' ? m.content : '' })) : [];
  const sources = Array.isArray(row.sources) === true ? row.sources : [];
  let runHistory = [];
  if (Array.isArray(row.runHistory) === true) {
    runHistory = row.runHistory.map((item, idx) => {
      const currentRunNumber = item !== null && item !== undefined && Number.isFinite(Number(item.runNumber)) === true ? Math.floor(Number(item.runNumber)) : idx + 1;
      const currentAutoPass = item !== null && item !== undefined ? (item.autoPass === true ? true : (item.autoPass === false ? false : null)) : null;
      const currentReason = item !== null && item !== undefined && item.autoReason !== undefined && item.autoReason !== null ? String(item.autoReason) : '';
      const currentAnswer = item !== null && item !== undefined && item.generatedAnswer !== undefined && item.generatedAnswer !== null ? String(item.generatedAnswer) : '';
      return {
        runNumber: currentRunNumber,
        autoPass: currentAutoPass,
        autoReason: currentReason,
        generatedAnswer: currentAnswer
      };
    });
  }
  return {
    testId: row.testId,
    testName: row.testName,
    judgePrompt: typeof row.judgePrompt === 'string' ? row.judgePrompt : '',
    config: normalizeTestConfig(row.config),
    messages,
    manualPass: row.manualPass === true ? true : (row.manualPass === false ? false : null),
    autoPass: row.autoPass === true ? true : (row.autoPass === false ? false : null),
    autoReason: row.autoReason,
    generatedAnswer: row.generatedAnswer,
    sources,
    running: false,
    deleting: false,
    runHistory
  };
}

function getRunTotalCount(row) {
  if (row === undefined || row === null) {
    return 0;
  }
  if (Array.isArray(row.runHistory) === true) {
    return row.runHistory.length;
  }
  if (row.autoPass === true || row.autoPass === false) {
    return 1;
  }
  return 0;
}

function getRunPassCount(row) {
  if (row === undefined || row === null) {
    return 0;
  }
  if (Array.isArray(row.runHistory) === true) {
    let count = 0;
    for (const item of row.runHistory) {
      if (item !== null && item !== undefined && item.autoPass === true) {
        count = count + 1;
      }
    }
    return count;
  }
  if (row.autoPass === true) {
    return 1;
  }
  return 0;
}

function getRunFailCount(row) {
  if (row === undefined || row === null) {
    return 0;
  }
  if (Array.isArray(row.runHistory) === true) {
    let count = 0;
    for (const item of row.runHistory) {
      if (item !== null && item !== undefined && item.autoPass === false) {
        count = count + 1;
      }
    }
    return count;
  }
  if (row.autoPass === false) {
    return 1;
  }
  return 0;
}

function getSafeRunPage(row) {
  const total = getRunTotalCount(row);
  if (total <= 0) {
    return 1;
  }
  const pageRaw = Number(selectedRunPage.value);
  if (Number.isFinite(pageRaw) !== true) {
    return 1;
  }
  const pageRounded = Math.floor(pageRaw);
  if (pageRounded < 1) {
    return 1;
  }
  if (pageRounded > total) {
    return total;
  }
  return pageRounded;
}

const activeRunHistoryItem = computed(() => {
  if (activeTest.value === null) {
    return null;
  }
  if (Array.isArray(activeTest.value.runHistory) !== true) {
    return null;
  }
  if (activeTest.value.runHistory.length === 0) {
    return null;
  }
  const page = getSafeRunPage(activeTest.value);
  return activeTest.value.runHistory[page - 1];
});

const activeRunPageText = computed(() => {
  const total = getRunTotalCount(activeTest.value);
  if (total <= 0) {
    return '0 / 0';
  }
  const page = getSafeRunPage(activeTest.value);
  return `${String(page)} / ${String(total)}`;
});

const activeRunGeneratedAnswer = computed(() => {
  if (activeRunHistoryItem.value !== null) {
    if (typeof activeRunHistoryItem.value.generatedAnswer === 'string') {
      return activeRunHistoryItem.value.generatedAnswer;
    }
  }
  if (activeTest.value === null) {
    return '';
  }
  if (activeTest.value.generatedAnswer === undefined || activeTest.value.generatedAnswer === null) {
    return '';
  }
  return String(activeTest.value.generatedAnswer);
});

const activeRunAutoReason = computed(() => {
  if (activeRunHistoryItem.value !== null) {
    if (typeof activeRunHistoryItem.value.autoReason === 'string') {
      return activeRunHistoryItem.value.autoReason;
    }
  }
  if (activeTest.value === null) {
    return '';
  }
  if (activeTest.value.autoReason === undefined || activeTest.value.autoReason === null) {
    return '';
  }
  return String(activeTest.value.autoReason);
});

function goToPreviousRunPage() {
  if (activeTest.value === null) {
    return;
  }
  const total = getRunTotalCount(activeTest.value);
  if (total <= 0) {
    return;
  }
  const page = getSafeRunPage(activeTest.value);
  if (page <= 1) {
    return;
  }
  selectedRunPage.value = page - 1;
}

function goToNextRunPage() {
  if (activeTest.value === null) {
    return;
  }
  const total = getRunTotalCount(activeTest.value);
  if (total <= 0) {
    return;
  }
  const page = getSafeRunPage(activeTest.value);
  if (page >= total) {
    return;
  }
  selectedRunPage.value = page + 1;
}

function createRunHistoryEntryFromTest(testRow, runNumber) {
  const currentAutoPass = testRow !== undefined && testRow !== null ? (testRow.autoPass === true ? true : (testRow.autoPass === false ? false : null)) : null;
  const currentReason = testRow !== undefined && testRow !== null && testRow.autoReason !== undefined && testRow.autoReason !== null ? String(testRow.autoReason) : '';
  const currentAnswer = testRow !== undefined && testRow !== null && testRow.generatedAnswer !== undefined && testRow.generatedAnswer !== null ? String(testRow.generatedAnswer) : '';
  return {
    runNumber,
    autoPass: currentAutoPass,
    autoReason: currentReason,
    generatedAnswer: currentAnswer
  };
}

function normalizeRunCount(rawValue) {
  const asNumber = Number(rawValue);
  if (Number.isFinite(asNumber) !== true) {
    return 1;
  }
  const rounded = Math.floor(asNumber);
  if (rounded < 1) {
    return 1;
  }
  if (rounded > 10) {
    return 10;
  }
  return rounded;
}

function resolveAggregateAutoPass(runHistory) {
  if (Array.isArray(runHistory) !== true) {
    return null;
  }
  if (runHistory.length === 0) {
    return null;
  }
  let hasUnknown = false;
  for (const item of runHistory) {
    if (item === undefined || item === null) {
      hasUnknown = true;
      continue;
    }
    if (item.autoPass === false) {
      return false;
    }
    if (item.autoPass !== true) {
      hasUnknown = true;
    }
  }
  if (hasUnknown === true) {
    return null;
  }
  return true;
}

async function loadEval() {
  if (props.evalId.length === 0) {
    return;
  }
  loadingEval.value = true;
  errorText.value = '';
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(props.evalId))}`, {
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      const data = await response.json();
      selectedEval.value = data.eval;
      editEvalName.value = data.eval.evalName;
      editAgentId.value = data.eval.agentId !== undefined && data.eval.agentId !== null ? [data.eval.agentId] : [];
      const rows = Array.isArray(data.tests) === true ? data.tests : [];
      tests.value = rows.map(r => {
        const mapped = mapTestRow(r);
        mapped.autoPass = null;
        mapped.manualPass = null;
        return mapped;
      });
    } else {
      selectedEval.value = null;
      tests.value = [];
      errorText.value = 'Failed to load eval';
    }
  } catch (e) {
    selectedEval.value = null;
    tests.value = [];
    errorText.value = 'Failed to load eval';
  } finally {
    loadingEval.value = false;
  }
}

const activeTestTitle = computed(() => {
  if (activeTest.value === null) {
    return 'Test';
  }
  const name = typeof activeTest.value.testName === 'string' ? activeTest.value.testName : '';
  return name.trim().length > 0 ? name : 'Test';
});

function openTest(t) {
  const clone = t !== undefined && t !== null ? JSON.parse(JSON.stringify(t)) : null;
  if (clone !== null) {
    clone.running = false;
    const total = getRunTotalCount(clone);
    selectedRunPage.value = total > 0 ? total : 1;
  }
  if (clone === null) {
    selectedRunPage.value = 1;
  }
  activeTest.value = clone;
  openTestModal.value = true;
}

function closeTest() {
  openTestModal.value = false;
  activeTest.value = null;
  selectedRunPage.value = 1;
}

async function downloadActiveTestPdf() {
  if (activeTest.value === null) {
    return;
  }
  if (pdfTargetRef.value === null || pdfTargetRef.value === undefined) {
    return;
  }
  try {
    const canvas = await html2canvas(pdfTargetRef.value, { scale: 2, backgroundColor: null, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = canvas.height * (imgWidth / canvas.width);
    let position = 0;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    let heightLeft = imgHeight - pageHeight;
    while (heightLeft > 0) {
      position = position - pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft = heightLeft - pageHeight;
    }
    const baseName = typeof activeTest.value.testName === 'string' && activeTest.value.testName.trim().length > 0 ? activeTest.value.testName.trim() : 'test';
    pdf.save(`${baseName}.pdf`);
  } catch (e) {
    return;
  }
}

async function downloadEvalPdf() {
  if (exportingEvalPdf.value === true) {
    return;
  }
  exportingEvalPdf.value = true;
  try {
    evalPdfGeneratedAt.value = new Date().toLocaleString();
    await nextTick();
    if (evalPdfTargetRef.value === null || evalPdfTargetRef.value === undefined) {
      exportingEvalPdf.value = false;
      return;
    }
    const canvas = await html2canvas(evalPdfTargetRef.value, { scale: 2, backgroundColor: null, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = canvas.height * (imgWidth / canvas.width);
    let position = 0;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    let heightLeft = imgHeight - pageHeight;
    while (heightLeft > 0) {
      position = position - pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft = heightLeft - pageHeight;
    }
    const baseName = typeof evalNameTitle.value === 'string' && evalNameTitle.value.trim().length > 0 ? evalNameTitle.value.trim() : 'eval';
    pdf.save(`${baseName}-results.pdf`);
  } catch (e) {
    exportingEvalPdf.value = false;
    return;
  }
  exportingEvalPdf.value = false;
}

function exportEvalJson() {
  if (exportingEvalJson.value === true) {
    return;
  }
  if (selectedEval.value === null) {
    return;
  }
  exportingEvalJson.value = true;
  try {
    const agentId = Array.isArray(editAgentId.value) === true && editAgentId.value.length > 0 ? editAgentId.value[0] : selectedEval.value.agentId;
    const payload = {
      eval: {
        evalId: selectedEval.value.evalId,
        evalName: typeof editEvalName.value === 'string' ? editEvalName.value : '',
        agentId: agentId !== undefined && agentId !== null ? agentId : ''
      },
      tests: Array.isArray(tests.value) === true ? tests.value.map(t => ({
        testId: t.testId,
        testName: typeof t.testName === 'string' ? t.testName : '',
        judgePrompt: typeof t.judgePrompt === 'string' ? t.judgePrompt : '',
        config: normalizeTestConfig(t.config),
        messages: Array.isArray(t.messages) === true
          ? t.messages.map(m => ({
              role: m.role === 'agent' ? 'agent' : 'user',
              content: typeof m.content === 'string' ? m.content : ''
            })).filter(m => m.content.trim().length > 0)
          : []
      })) : []
    };
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const baseName = typeof evalNameTitle.value === 'string' && evalNameTitle.value.trim().length > 0 ? evalNameTitle.value.trim() : 'eval';
    link.href = url;
    link.download = `${baseName}.json`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    errorText.value = 'Failed to export JSON';
  } finally {
    exportingEvalJson.value = false;
  }
}

function openImportPicker() {
  if (importingEvalJson.value === true) {
    return;
  }
  if (importFileInputRef.value !== null && importFileInputRef.value !== undefined) {
    importFileInputRef.value.value = '';
    importFileInputRef.value.click();
  }
}

async function handleImportFile(event) {
  if (importingEvalJson.value === true) {
    return;
  }
  if (selectedEval.value === null) {
    return;
  }
  const file = event !== undefined && event !== null && event.target !== undefined ? event.target.files?.[0] : null;
  if (file === null || file === undefined) {
    return;
  }
  importingEvalJson.value = true;
  errorText.value = '';
  try {
    const text = await file.text();
    const data = JSON.parse(text);
    const evalData = data !== null && data !== undefined ? data.eval : null;
    if (evalData !== null && evalData !== undefined) {
      if (typeof evalData.evalName === 'string' && evalData.evalName.trim().length > 0) {
        editEvalName.value = evalData.evalName.trim();
      }
      if (evalData.agentId !== undefined && evalData.agentId !== null && String(evalData.agentId).trim().length > 0) {
        editAgentId.value = [String(evalData.agentId)];
      }
      await saveEvalDetails();
    }
    const rawTests = data !== null && data !== undefined && Array.isArray(data.tests) === true ? data.tests : [];
    for (const raw of rawTests) {
      const testName = typeof raw?.testName === 'string' ? raw.testName.trim() : '';
      const judgePrompt = typeof raw?.judgePrompt === 'string' ? raw.judgePrompt : '';
      const messages = Array.isArray(raw?.messages) === true
        ? raw.messages.map(m => ({
            role: m?.role === 'agent' ? 'agent' : 'user',
            content: typeof m?.content === 'string' ? m.content : ''
          })).filter(m => m.content.trim().length > 0)
        : [];
      const config = normalizeTestConfig(raw?.config);
      const qaVoiceTest = config.runnerType === 'voice' && config.voice.strategy === 'qa' && config.voice.qaPrompt.trim().length > 0;
      if (testName.length === 0 && messages.length === 0 && qaVoiceTest !== true) {
        continue;
      }
      const payload = {
        testName: testName.length > 0 ? testName : 'Imported Test',
        judgePrompt,
        messages,
        config
      };
      const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(selectedEval.value.evalId))}/tests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (response.ok === true) {
        const created = await response.json();
        tests.value.unshift(mapTestRow(created));
      } else {
        errorText.value = 'Failed to import tests';
      }
    }
    emit('refreshList');
  } catch (e) {
    errorText.value = 'Failed to import JSON';
  } finally {
    importingEvalJson.value = false;
  }
}

function openEditModal() {
  if (activeTest.value === null) {
    return;
  }
  const src = activeTest.value;
  const draft = {
    testId: src.testId,
    testName: typeof src.testName === 'string' ? src.testName : '',
    judgePrompt: typeof src.judgePrompt === 'string' ? src.judgePrompt : '',
    config: normalizeTestConfig(src.config),
    messages: Array.isArray(src.messages) === true ? src.messages.map(m => ({ role: m.role === 'agent' ? 'agent' : 'user', content: typeof m.content === 'string' ? m.content : '' })) : [],
  };
  editDraft.value = draft;
  openEditTestModal.value = true;
}

function closeEditModal() {
  openEditTestModal.value = false;
  editDraft.value = null;
}

async function saveDraft() {
  if (selectedEval.value === null) {
    return;
  }
  if (editDraft.value === null) {
    return;
  }
  if (savingDraft.value === true) {
    return;
  }
  savingDraft.value = true;
  errorText.value = '';
  try {
    const draft = editDraft.value;
    const msgs = Array.isArray(draft.messages) === true ? draft.messages : [];
    const cleaned = msgs
      .map(m => ({ role: m.role === 'agent' ? 'agent' : 'user', content: typeof m.content === 'string' ? m.content : '' }))
      .filter(m => m.content.trim().length > 0);
    const payload = {
      testName: typeof draft.testName === 'string' ? draft.testName : '',
      judgePrompt: typeof draft.judgePrompt === 'string' ? draft.judgePrompt : '',
      messages: cleaned,
      config: normalizeTestConfig(draft.config),
      manualPass: activeTest.value !== null ? (activeTest.value.manualPass === true ? true : (activeTest.value.manualPass === false ? false : null)) : null
    };
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(selectedEval.value.evalId))}/tests/${encodeURIComponent(String(draft.testId))}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (response.ok === true) {
      const updated = await response.json();
      const mapped = mapTestRow(updated);
      const idx = tests.value.findIndex(x => x.testId === mapped.testId);
      if (idx >= 0) {
        tests.value[idx] = mapped;
      }
      activeTest.value = mapped;
      editDraft.value = {
        testId: mapped.testId,
        testName: typeof mapped.testName === 'string' ? mapped.testName : '',
        judgePrompt: typeof mapped.judgePrompt === 'string' ? mapped.judgePrompt : '',
        config: normalizeTestConfig(mapped.config),
        messages: Array.isArray(mapped.messages) === true ? mapped.messages.map(m => ({ role: m.role === 'agent' ? 'agent' : 'user', content: typeof m.content === 'string' ? m.content : '' })) : [],
      };
      emit('refreshList');
      openEditTestModal.value = false;
      editDraft.value = null;
    } else {
      errorText.value = 'Failed to save test';
    }
  } catch (e) {
    errorText.value = 'Failed to save test';
  } finally {
    savingDraft.value = false;
  }
}

async function runAll() {
  if (props.evalId.length === 0) {
    return;
  }
  if (runningEval.value === true) {
    return;
  }
  runningEval.value = true;
  errorText.value = '';
  showRunProgressModal.value = true;
  const runCount = normalizeRunCount(selectedRunCount.value);
  runTotal.value = Array.isArray(tests.value) === true ? tests.value.length * runCount : 0;
  runCompleted.value = 0;
  runCurrentTestName.value = '';
  runCurrentIteration.value = 0;
  try {
    const list = Array.isArray(tests.value) === true ? tests.value.slice() : [];
    const tasks = [];
    for (let i = 0; i < list.length; i = i + 1) {
      const t = list[i];
      if (t !== undefined && t !== null && t.testId !== undefined && t.testId !== null) {
        tasks.push(runOne(t, runCount, () => {
          runCompleted.value = runCompleted.value + 1;
        }));
      }
    }
    if (tasks.length > 0) {
      await Promise.all(tasks);
    }
  } catch (e) {
    errorText.value = 'Failed to run eval';
  } finally {
    runCurrentIteration.value = 0;
    runningEval.value = false;
  }
}

async function runOne(t, runCountArg, onAttemptCompleted) {
  if (props.evalId.length === 0) {
    return;
  }
  if (t !== undefined && t !== null) {
    if (t.running === true) {
      return;
    }
    t.running = true;
    errorText.value = '';
    const runCount = normalizeRunCount(runCountArg !== undefined && runCountArg !== null ? runCountArg : selectedRunCount.value);
    const runHistory = [];
    let latestMapped = null;
    const attemptTasks = [];
    for (let runIndex = 1; runIndex <= runCount; runIndex = runIndex + 1) {
      attemptTasks.push((async () => {
        runCurrentTestName.value = typeof t.testName === 'string' ? t.testName : '';
        runCurrentIteration.value = runIndex;
        let mappedAttempt = null;
        let runItem = {
          runNumber: runIndex,
          autoPass: false,
          autoReason: 'Failed to run test',
          generatedAnswer: ''
        };
        try {
          const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(props.evalId))}/tests/${encodeURIComponent(String(t.testId))}/run`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include'
          });
          if (response.ok === true) {
            const data = await response.json();
            if (data !== null && data !== undefined && data.test !== undefined && data.test !== null) {
              mappedAttempt = mapTestRow(data.test);
              runItem = createRunHistoryEntryFromTest(mappedAttempt, runIndex);
            } else {
              runItem = {
                runNumber: runIndex,
                autoPass: false,
                autoReason: 'Invalid test response',
                generatedAnswer: ''
              };
            }
          } else {
            errorText.value = 'Failed to run test';
          }
        } catch (e) {
          errorText.value = 'Failed to run test';
        } finally {
          if (typeof onAttemptCompleted === 'function') {
            onAttemptCompleted();
          }
        }
        return { runNumber: runIndex, mappedAttempt, runItem };
      })());
    }
    const attemptResults = await Promise.all(attemptTasks);
    const orderedAttempts = attemptResults.slice().sort((a, b) => a.runNumber - b.runNumber);
    for (const attempt of orderedAttempts) {
      runHistory.push(attempt.runItem);
      if (attempt.mappedAttempt !== null) {
        latestMapped = attempt.mappedAttempt;
      }
    }
    try {
      let mapped = null;
      if (latestMapped !== null) {
        mapped = latestMapped;
      } else {
        mapped = mapTestRow({
          testId: t.testId,
          testName: t.testName,
          judgePrompt: t.judgePrompt,
          messages: Array.isArray(t.messages) === true ? t.messages : [],
          manualPass: t.manualPass,
          autoPass: t.autoPass,
          autoReason: t.autoReason,
          generatedAnswer: t.generatedAnswer,
          sources: Array.isArray(t.sources) === true ? t.sources : []
        });
      }
      mapped.running = false;
      mapped.runHistory = runHistory;
      mapped.autoPass = resolveAggregateAutoPass(runHistory);
      mapped.manualPass = null;
      const idx = tests.value.findIndex(x => x.testId === t.testId);
      if (idx >= 0) {
        tests.value[idx] = mapped;
        if (openTestModal.value === true && activeTest.value !== null && activeTest.value.testId === t.testId) {
          activeTest.value = tests.value[idx];
          const total = getRunTotalCount(activeTest.value);
          selectedRunPage.value = total > 0 ? total : 1;
        }
      }
      emit('refreshList');
      runCurrentIteration.value = 0;
    } catch (e) {
      errorText.value = 'Failed to run test';
      runCurrentIteration.value = 0;
    } finally {
      t.running = false;
    }
  }
}

const progressPercent = computed(() => {
  const total = Number.isFinite(Number(runTotal.value)) === true ? Number(runTotal.value) : 0;
  const done = Number.isFinite(Number(runCompleted.value)) === true ? Number(runCompleted.value) : 0;
  if (total <= 0) {
    return 0;
  }
  const pct = Math.round((done / total) * 100);
  return pct < 0 ? 0 : (pct > 100 ? 100 : pct);
});

const runLabel = computed(() => {
  const total = Number.isFinite(Number(runTotal.value)) === true ? Number(runTotal.value) : 0;
  const done = Number.isFinite(Number(runCompleted.value)) === true ? Number(runCompleted.value) : 0;
  const name = typeof runCurrentTestName.value === 'string' ? runCurrentTestName.value.trim() : '';
  if (runningEval.value === true) {
    if (name.length > 0) {
      const runNumber = Number.isFinite(Number(runCurrentIteration.value)) === true ? Number(runCurrentIteration.value) : 0;
      if (runNumber > 0) {
        return `Running: ${name} (${runNumber}/${normalizeRunCount(selectedRunCount.value)})`;
      }
      return `Running: ${name}`;
    }
    return 'Running...';
  }
  if (total > 0 && done >= total) {
    return 'Done';
  }
  return '';
});

function closeRunProgressModal() {
  if (runningEval.value === true) {
    return;
  }
  showRunProgressModal.value = false;
}

async function saveEvalDetails() {
  if (selectedEval.value === null) {
    return;
  }
  if (savingEval.value === true) {
    return;
  }
  savingEval.value = true;
  errorText.value = '';
  try {
    const agentId = Array.isArray(editAgentId.value) === true && editAgentId.value.length > 0 ? editAgentId.value[0] : null;
    const payload = {
      evalName: editEvalName.value,
      agentId: agentId
    };
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(selectedEval.value.evalId))}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (response.ok === true) {
      const updated = await response.json();
      selectedEval.value = updated;
      emit('refreshList');
    } else {
      errorText.value = 'Failed to save eval details';
    }
  } catch (e) {
    errorText.value = 'Failed to save eval details';
  } finally {
    savingEval.value = false;
  }
}

async function removeEval() {
  if (selectedEval.value === null) {
    return;
  }
  if (deletingEval.value === true) {
    return;
  }
  deletingEval.value = true;
  errorText.value = '';
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(selectedEval.value.evalId))}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      emit('refreshList');
      emit('back');
    } else {
      errorText.value = 'Failed to delete eval';
    }
  } catch (e) {
    errorText.value = 'Failed to delete eval';
  } finally {
    deletingEval.value = false;
  }
}

async function generateTestsWithAI() {
  if (selectedEval.value === null) {
    return;
  }
  if (generatingTests.value === true) {
    return;
  }
  generatingTests.value = true;
  errorText.value = '';
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(selectedEval.value.evalId))}/generate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      const data = await response.json();
      const newTests = Array.isArray(data.tests) === true ? data.tests : [];
      for (const t of newTests) {
        tests.value.unshift(mapTestRow(t));
      }
      emit('refreshList');
    } else {
      const err = await response.json();
      errorText.value = err.error || 'Failed to generate tests';
    }
  } catch (e) {
    errorText.value = 'Failed to generate tests';
  } finally {
    generatingTests.value = false;
  }
}

async function addTest() {
  if (selectedEval.value === null) {
    return;
  }
  if (savingTest.value === true) {
    return;
  }
  savingTest.value = true;
  errorText.value = '';
  try {
    const baseName = `Test ${tests.value.length + 1}`;
    const payload = {
      testName: baseName,
      judgePrompt: 'Return pass: true if the response is helpful.',
      messages: [{ role: 'user', content: 'Hello' }],
      config: normalizeTestConfig(null)
    };
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(selectedEval.value.evalId))}/tests`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (response.ok === true) {
      const created = await response.json();
      const mapped = mapTestRow(created);
      tests.value.unshift(mapped);
      openTest(mapped);
      emit('refreshList');
    } else {
      errorText.value = 'Failed to add test';
    }
  } catch (e) {
    errorText.value = 'Failed to add test';
  } finally {
    savingTest.value = false;
  }
}

async function saveTest(t) {
  if (selectedEval.value === null) {
    return;
  }
  if (t === undefined || t === null) {
    return;
  }
  errorText.value = '';
  const msgs = Array.isArray(t.messages) === true ? t.messages : [];
  const cleaned = msgs
    .map(m => ({ role: m.role === 'agent' ? 'agent' : 'user', content: typeof m.content === 'string' ? m.content : '' }))
    .filter(m => m.content.trim().length > 0);
  const payload = {
    testName: t.testName,
    judgePrompt: t.judgePrompt,
    messages: cleaned,
    config: normalizeTestConfig(t.config),
    manualPass: t.manualPass === true ? true : (t.manualPass === false ? false : null)
  };
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(selectedEval.value.evalId))}/tests/${encodeURIComponent(String(t.testId))}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (response.ok === true) {
      const updated = await response.json();
      const mapped = mapTestRow(updated);
      mapped.deleting = t.deleting === true;
      const idx = tests.value.findIndex(x => x.testId === t.testId);
      if (idx >= 0) {
        tests.value[idx] = mapped;
        if (openTestModal.value === true && activeTest.value !== null && activeTest.value.testId === t.testId) {
          activeTest.value = tests.value[idx];
          const total = getRunTotalCount(activeTest.value);
          selectedRunPage.value = total > 0 ? total : 1;
        }
      }
      emit('refreshList');
    } else {
      errorText.value = 'Failed to save test';
    }
  } catch (e) {
    errorText.value = 'Failed to save test';
  }
}

function addMessage(t, role) {
  const target = t !== undefined && t !== null && t.value !== undefined ? t.value : t;
  if (target !== undefined && target !== null) {
    if (Array.isArray(target.messages) === false) {
      target.messages = [];
    }
    const r = role === 'agent' ? 'agent' : 'user';
    target.messages.push({ role: r, content: '' });
  }
}

function removeMessage(t, idx) {
  const target = t !== undefined && t !== null && t.value !== undefined ? t.value : t;
  if (target !== undefined && target !== null && Array.isArray(target.messages) === true) {
    if (idx >= 0 && idx < target.messages.length) {
      target.messages.splice(idx, 1);
    }
  }
}

async function removeTest(t) {
  if (selectedEval.value === null) {
    return;
  }
  if (t.deleting === true) {
    return;
  }
  t.deleting = true;
  errorText.value = '';
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals/${encodeURIComponent(String(selectedEval.value.evalId))}/tests/${encodeURIComponent(String(t.testId))}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      tests.value = tests.value.filter(x => x.testId !== t.testId);
      emit('refreshList');
    } else {
      errorText.value = 'Failed to delete test';
    }
  } catch (e) {
    errorText.value = 'Failed to delete test';
  } finally {
    t.deleting = false;
  }
}

async function toggleManual(t, val) {
  if (t !== undefined && t !== null) {
    t.manualPass = val === true ? true : false;
    await saveTest(t);
  }
}

const overallPass = computed(() => {
  if (selectedEval.value === null) {
    return null;
  }
  if (tests.value.length === 0) {
    return null;
  }
  let failed = false;
  let hasAny = false;
  for (const t of tests.value) {
    const manual = t.manualPass === true ? true : (t.manualPass === false ? false : null);
    const auto = t.autoPass === true ? true : (t.autoPass === false ? false : null);
    const resolved = manual !== null ? manual : auto;
    if (resolved !== null) {
      hasAny = true;
    }
    if (resolved === false) {
      failed = true;
    }
  }
  if (hasAny === false) {
    return null;
  }
  return failed === true ? false : true;
});

const overallPassLabel = computed(() => {
  if (overallPass.value === true) {
    return 'Pass';
  }
  if (overallPass.value === false) {
    return 'Fail';
  }
  return 'Not evaluated';
});

const latestRunAtText = computed(() => {
  if (Array.isArray(tests.value) === false) {
    return '';
  }
  let maxTs = null;
  for (const t of tests.value) {
    const raw = t !== undefined && t !== null && t.lastRunAt !== undefined && t.lastRunAt !== null ? String(t.lastRunAt) : '';
    if (raw.trim().length > 0) {
      const d = new Date(raw);
      const ts = Number.isFinite(d.getTime()) === true ? d.getTime() : null;
      if (ts !== null) {
        if (maxTs === null || ts > maxTs) {
          maxTs = ts;
        }
      }
    }
  }
  if (maxTs === null) {
    return '';
  }
  return formatDate(new Date(maxTs).toISOString());
});

const headerStatusText = computed(() => {
  if (overallPass.value === true) {
    return t('agentEvals.passed', 'Passed');
  }
  if (overallPass.value === false) {
    return t('agentEvals.notPassed', 'Not passed');
  }
  return '';
});

const evalNameTitle = computed(() => {
  if (selectedEval.value !== null && selectedEval.value.evalName !== undefined) {
    return selectedEval.value.evalName;
  }
  return 'Agent Eval';
});

watch(() => props.evalId, async () => {
  await loadEval();
}, { immediate: true });

onMounted(async () => {
  await loadEval();
});
</script>

<style scoped>
@media (min-width: 1024px) {
  .col-gap {
    margin-inline-start: 1.5rem;
  }
}
</style>


